// Pages/actividadClientes/actividadClientes.tsx
// Historial de actividad de clientes (admin) — round 2026-07-25.
// Tabla paginada server-side: logins, navegación y consultas de los usuarios
// con rol cliente. Mismo estilo que /historial. Ver docs/modulo-actividad-clientes.md.
import React, { useEffect, useMemo, useState } from "react";
import "./actividadClientes.css";
import {
  Table,
  Tag,
  Space,
  Input,
  DatePicker,
  Select,
  Typography,
  Tooltip,
  Button,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { formatDate } from "../../utils/formatDate";
import { useSidebar } from "../../hooks/useSidebar";
import { CADUCIDAD_FECHAS_DIAS } from "../../hooks/filtrosPersistidos";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  actividadClientesApi,
  ActividadRecord,
  ActividadTipo,
} from "../../services/actividadClientes.api";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { UserCell } from "../../Components/UserAvatar";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Config visual por tipo de evento.
const TIPO_CONFIG: Record<ActividadTipo, { color: string; label: string }> = {
  login: { color: "blue", label: "Inicio de sesión" },
  navegacion: { color: "geekblue", label: "Navegación" },
  consulta: { color: "default", label: "Consulta" },
  creacion: { color: "green", label: "Creación" },
  edicion: { color: "gold", label: "Edición" },
  eliminacion: { color: "red", label: "Eliminación" },
};

// Tipos que son mutaciones (CRUD): llevan descripción legible + detalle técnico.
const TIPOS_MUTACION: ActividadTipo[] = ["creacion", "edicion", "eliminacion"];

// Color del status HTTP: 2xx ok, 3xx info, 4xx alerta, 5xx error.
function statusColor(code: number | null): string {
  if (code == null) return "default";
  if (code >= 500) return "red";
  if (code >= 400) return "orange";
  if (code >= 300) return "blue";
  if (code >= 200) return "green";
  return "default";
}

// Fecha + HORA, para el filtro de rango de esta página.
//
// Su RangePicker lleva `showTime`, así que la hora elegida ES parte del filtro:
// viaja como instante exacto en `from`/`to` y el backend compara
// created_at >= desde AND created_at <= hasta. Los helpers compartidos guardan
// solo "YYYY-MM-DD" (lo correcto para los pickers sin hora del resto del
// proyecto), y con eso "24-09 09:00 → 24-09 18:00" volvía como
// "24-09 00:00 → 24-09 00:00": un rango de ancho cero, tabla vacía y un picker
// que muestra una selección de aspecto legítimo. En una página de auditoría eso
// se lee como "este cliente no hizo nada ese día". Acá se guarda el instante.

const FORMATO_INSTANTE = "YYYY-MM-DD HH:mm:ss";
const ES_INSTANTE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const ES_DIA = /^\d{4}-\d{2}-\d{2}$/;

const serializarInstante = (valor: Dayjs | null | undefined): string | null =>
  valor && dayjs.isDayjs(valor) && valor.isValid()
    ? valor.format(FORMATO_INSTANTE)
    : null;

const deserializarInstante = (crudo: unknown): Dayjs | null | undefined => {
  if (crudo === null) return null;
  if (typeof crudo !== "string") return undefined;
  // Compat: lo que quedó guardado antes de este arreglo trae solo el día.
  const formato = ES_INSTANTE.test(crudo)
    ? FORMATO_INSTANTE
    : ES_DIA.test(crudo)
      ? "YYYY-MM-DD"
      : null;
  if (!formato) return undefined;
  const d = dayjs(crudo);
  // `isValid()` no alcanza: dayjs("2026-13-45") no falla, lo corre a 2027-02-14.
  // Solo se acepta si vuelve a escribirse igual que como se guardó.
  return d.isValid() && d.format(formato) === crudo ? d : undefined;
};

const ActividadClientes: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();

  const [data, setData] = useState<ActividadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);

  // filtros UI — se recuerdan por usuario admin en este navegador
  // (round 2026-09-24, ver docs/filtros-persistentes.md).
  const [q, setQ] = useFiltroPersistido<string | undefined>(
    "actividadClientes.busqueda",
    undefined,
    { validar: (v) => v === undefined || typeof v === "string" },
  ); // término aplicado (dispara refetch)
  // Texto del buscador. NO se persiste por separado: es un espejo de `q` y si
  // divergieran se vería la caja escrita con la tabla sin filtrar (o al revés).
  const [searchText, setSearchText] = useState<string>(() => q ?? ""); // texto controlado del buscador
  useEffect(() => {
    setSearchText(q ?? "");
  }, [q]);
  const [tipo, setTipo] = useFiltroPersistido<ActividadTipo | undefined>(
    "actividadClientes.tipo",
    undefined,
    {
      // `hasOwnProperty` y no `in`: `in` acepta lo heredado del prototipo
      // ("toString", "constructor") y esa basura terminaría viajando al backend
      // como filtro de tipo.
      validar: (v) =>
        v === undefined ||
        (typeof v === "string" &&
          Object.prototype.hasOwnProperty.call(TIPO_CONFIG, v)),
    },
  );
  const [range, setRange] = useFiltroPersistido<[Dayjs, Dayjs] | null>(
    "actividadClientes.rango",
    null,
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: (r) =>
        r ? [serializarInstante(r[0]), serializarInstante(r[1])] : null,
      deserializar: (crudo) => {
        if (crudo === null) return null;
        if (!Array.isArray(crudo) || crudo.length !== 2) return undefined;
        const desde = deserializarInstante(crudo[0]);
        const hasta = deserializarInstante(crudo[1]);
        if (desde === undefined || hasta === undefined) return undefined;
        // El filtro necesita las dos puntas: media fecha no filtra nada.
        return desde && hasta ? ([desde, hasta] as [Dayjs, Dayjs]) : null;
      },
    },
  );

  const fetchData = async (p = 1, s = size) => {
    try {
      setLoading(true);
      const json = await actividadClientesApi.list({
        page: p,
        size: s,
        q: q || undefined,
        tipo: tipo || undefined,
        from: range ? range[0].toISOString() : undefined,
        to: range ? range[1].toISOString() : undefined,
      });
      if (!json) return;
      setData(json.data || []);
      setTotal(json.total || 0);
      setPage(json.page || 1);
      setSize(json.size || 20);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tipo, range]);

  const columns: ColumnsType<ActividadRecord> = useMemo(
    () => [
      {
        title: "Fecha y hora",
        dataIndex: "created_at",
        width: 170,
        // Sin `sorter` client-side: la tabla es paginada server-side y el
        // backend ya ordena por created_at DESC (más reciente primero). Un
        // sorter en memoria solo reordenaría la página visible → engañoso.
        render: (v) => formatDate(v, "DD-MM-YYYY HH:mm"),
      },
      {
        title: "Cliente",
        dataIndex: "user_nombre",
        width: 240,
        render: (nombre: string | null, r) => (
          <UserCell
            nombre={nombre || "Usuario eliminado"}
            secondary={r.user_email || undefined}
            size={30}
          />
        ),
      },
      {
        title: "Tipo",
        dataIndex: "tipo",
        width: 150,
        render: (t: ActividadTipo) => {
          const cfg = TIPO_CONFIG[t] || { color: "default", label: t };
          return <Tag color={cfg.color}>{cfg.label}</Tag>;
        },
      },
      {
        title: "Detalle",
        key: "detalle",
        render: (_, r) => {
          if (r.tipo === "login") {
            return <Text>Inició sesión en la plataforma</Text>;
          }
          if (r.tipo === "navegacion") {
            return (
              <Space direction="vertical" size={0}>
                <Text strong>{r.descripcion || r.ruta || "—"}</Text>
                {r.descripcion && r.ruta ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.ruta}
                  </Text>
                ) : null}
              </Space>
            );
          }
          // Detalle técnico (método + ruta + status), compartido por consultas
          // y mutaciones.
          const tecnico = (
            <Space size={6} wrap>
              {r.metodo ? <Tag color="cyan">{r.metodo}</Tag> : null}
              <Text code style={{ fontSize: 12 }}>
                {r.ruta || "—"}
              </Text>
              {r.status_code != null ? (
                <Tag color={statusColor(r.status_code)}>{r.status_code}</Tag>
              ) : null}
            </Space>
          );
          // Mutación (creación/edición/eliminación): descripción legible arriba
          // + el detalle técnico como secundaria.
          if (TIPOS_MUTACION.includes(r.tipo)) {
            return (
              <Space direction="vertical" size={2}>
                <Text strong>{r.descripcion || "Acción"}</Text>
                {tecnico}
              </Space>
            );
          }
          // consulta
          return tecnico;
        },
      },
      {
        title: "IP",
        dataIndex: "ip",
        width: 140,
        render: (ip: string | null) =>
          ip ? (
            <Text style={{ fontSize: 12 }}>{ip}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Dispositivo",
        dataIndex: "user_agent",
        width: 220,
        ellipsis: { showTitle: false },
        render: (ua: string | null) =>
          ua ? (
            <Tooltip title={ua}>
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {ua}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
    ],
    [],
  );

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="actividad-page"
    >
      <div style={{ margin: "0 24px" }}>
        <PageHeader
          title="Actividad de clientes"
          helpText="Historial completo de lo que hacen los clientes: inicios de sesión, navegación, consultas y todo el CRUD — qué crearon, editaron o eliminaron, incluidos los bloqueos y mantenciones del calendario con su propiedad, fechas y motivo. Con fecha, hora, IP y dispositivo."
        />
      </div>
      <div className="p-4">
        <Space
          className="actividad-filters"
          wrap
          size="middle"
          style={{ marginBottom: 12 }}
        >
          <Input.Search
            allowClear
            placeholder="Buscar por cliente, email, ruta o página"
            value={searchText}
            onChange={(e) => {
              const v = e.target.value;
              setSearchText(v);
              // Al vaciar el input (X de allowClear o borrar todo) quita el
              // filtro aplicado sin tener que presionar Enter.
              if (!v) setQ(undefined);
            }}
            onSearch={(v) => setQ(v || undefined)}
            style={{ width: 320 }}
          />
          <Select
            allowClear
            placeholder="Tipo de evento"
            value={tipo}
            onChange={(v) => setTipo(v)}
            options={[
              { value: "login", label: "Inicio de sesión" },
              { value: "navegacion", label: "Navegación" },
              { value: "consulta", label: "Consulta" },
              { value: "creacion", label: "Creación" },
              { value: "edicion", label: "Edición" },
              { value: "eliminacion", label: "Eliminación" },
            ]}
            style={{ width: 200 }}
          />
          <RangePicker
            showTime
            value={range}
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setRange([vals[0], vals[1]]);
              } else {
                setRange(null);
              }
            }}
          />
          <Button
            onClick={() => {
              setSearchText("");
              setQ(undefined);
              setTipo(undefined);
              setRange(null);
            }}
          >
            Limpiar
          </Button>
        </Space>

        <Table<ActividadRecord>
          className="actividad-table"
          rowKey="id"
          loading={loading}
          dataSource={data}
          columns={columns}
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
            showTotal: (t) => `${t} registros`,
            onChange: (p, s) => fetchData(p, s),
          }}
          scroll={{ x: 1100 }}
          size="middle"
        />
      </div>
    </Layout>
  );
};

export default ActividadClientes;
